/**
 * The calendar page's pure decisions (1.O-3 deliverable 1): where a season starts, how an import
 * merges with the stored calendar, what each warning, issue and review reason says, how a diff is
 * summarized, which removed keys ask where their dates go, and when "Commit calendar" is on.
 * Nothing here reads the DOM, the network or the clock.
 */

import { inferRemap } from '@codex/calendar/diff';
import { type CompiledCalendar, compile } from '@codex/calendar/engine';
import { formatTick } from '@codex/calendar/format';
import {
  type CALENDAR_ERROR_CODES,
  type CALENDAR_WARNING_CODES,
  type CalendarDefinition,
  type CalendarDiffV1,
  type CalendarRemap,
  type CalendarV1,
  type FOUNDRY_IMPORT_WARNING_CODES,
  type FoundryImportWarning,
  TICK_REVIEW_REASONS,
  type TickResultV1,
  type TickReviewReason,
} from '@codex/contracts';

// ------------------------------------------------------------------------------------ seasons

export interface SeasonFirstDay {
  readonly key: string;
  readonly name: string;
  /** The month the season's first day falls in, or null when the start lies past the year. */
  readonly monthKey: string | null;
  readonly month: string | null;
  /** 1-based day of that month, or null as above. */
  readonly day: number | null;
}

/** The month and day (1-based) of a 0-based day of the common year, in list order of months. */
export function monthDayOfYearDay(
  definition: CalendarDefinition,
  startDay: number,
): { monthKey: string; month: string; day: number } | null {
  if (!Number.isInteger(startDay) || startDay < 0) return null;
  let before = 0;
  for (const month of definition.months) {
    if (startDay < before + month.days) {
      return { monthKey: month.key, month: month.name, day: startDay - before + 1 };
    }
    before += month.days;
  }
  return null;
}

/** Each season's first day, in list order; the month and day are null past the year's end. */
export function seasonFirstDays(definition: CalendarDefinition): SeasonFirstDay[] {
  return definition.seasons.map((season) => {
    const first = monthDayOfYearDay(definition, season.startDay);
    return {
      key: season.key,
      name: season.name,
      monthKey: first?.monthKey ?? null,
      month: first?.month ?? null,
      day: first?.day ?? null,
    };
  });
}

function dayText(first: { month: string; day: number }): string {
  return `${first.day} ${first.month}`;
}

export interface SeasonRange {
  readonly key: string;
  readonly name: string;
  /** "from 1 Deep to 36 Thaw (wraps the year end)", or null past the year. */
  readonly text: string | null;
}

function yearDaysOf(definition: CalendarDefinition): number {
  return definition.months.reduce((sum, month) => sum + month.days, 0);
}

/**
 * Per season "from <first day> to <day before the next start>": the next start taken in
 * `startDay` order, the last season running to the first season's start across the year end (the
 * engine's own rule, PLAN section 7).
 */
export function seasonRanges(definition: CalendarDefinition): SeasonRange[] {
  const yearDays = yearDaysOf(definition);
  const order = definition.seasons
    .map((season, index) => ({ season, index }))
    .filter(({ season }) => season.startDay < yearDays)
    .sort((a, b) => a.season.startDay - b.season.startDay || a.index - b.index);
  const texts = new Map<number, string>();
  order.forEach(({ season, index }, position) => {
    const first = monthDayOfYearDay(definition, season.startDay);
    const last = position === order.length - 1;
    const next = order[last ? 0 : position + 1];
    if (!first || !next) return;
    const wraps = last && next.season.startDay > 0;
    const endDay = last && !wraps ? yearDays - 1 : next.season.startDay - 1;
    const end = monthDayOfYearDay(definition, endDay) ?? first;
    texts.set(
      index,
      `from ${dayText(first)} to ${dayText(end)}${wraps ? ' (wraps the year end)' : ''}`,
    );
  });
  return definition.seasons.map((season, index) => ({
    key: season.key,
    name: season.name,
    text: texts.get(index) ?? null,
  }));
}

// -------------------------------------------------------------------------------------- merge

/**
 * The draft an import makes. With `keep` off, or no calendar yet, the imported definition as it
 * is. Otherwise the imported one with the stored eras, `approx` and time offset, and the stored
 * moons when the file has none (Foundry's format carries none of these; ruling b).
 */
export function mergeImport(
  current: CalendarDefinition | null,
  imported: CalendarDefinition,
  keep: boolean,
): CalendarDefinition {
  if (!keep || current === null) return imported;
  return {
    ...imported,
    eras: current.eras,
    approx: current.approx,
    moons: imported.moons.length === 0 ? current.moons : imported.moons,
    foundry: { ...imported.foundry, worldTimeOffset: current.foundry.worldTimeOffset },
  };
}

// --------------------------------------------------------------------------------------- texts

/** Fixed copy around a season's number: never a name the GM wrote. */
export function importWarningText(warning: FoundryImportWarning): string {
  const season =
    warning.seasonIndex === undefined ? 'A season' : `Season ${warning.seasonIndex + 1}`;
  const texts: Record<(typeof FOUNDRY_IMPORT_WARNING_CODES)[number], string> = {
    leap_rule_dormant:
      'The file has a leap rule, but no month has a leap day, so it changes nothing.',
    days_per_year_mismatch: "The file's days per year don't match the sum of its months.",
    season_bounds_touch: `${season} ends on the day the next one starts, so that day is in both.`,
    season_gap: `${season} ends well before the next one starts, leaving days in no season.`,
    season_overlap: `${season} runs past the start of the next one.`,
    invalid_calendar: warning.path
      ? `Foundry can't read part of this file (at ${warning.path}).`
      : "Foundry can't read part of this file.",
  };
  return texts[warning.code];
}

const ISSUE_TEXTS: Record<
  (typeof CALENDAR_ERROR_CODES)[number] | (typeof CALENDAR_WARNING_CODES)[number],
  string
> = {
  invalid_shape: "This value isn't allowed here.",
  no_regular_month: 'A calendar needs at least one month that is not a festival day.',
  duplicate_ordinal: 'Two months have the same number.',
  bad_month_length: 'A month has a length it cannot have.',
  bad_season_start: "A season starts after the year's last day.",
  bad_era: 'Give this era a name or a suffix: only one era may have neither',
  calendar_too_large: 'The year is too long for Codex to count in.',
  leap_rule_dormant: 'The leap rule changes nothing, because no month has a leap day.',
  day_offset_ignored: "A month's day offset is kept for Foundry but Codex ignores it.",
  seasons_out_of_order: "The seasons aren't listed in the order they start.",
  starting_weekday_inconsistent: "A month's starting weekday doesn't match the week's count.",
  ambiguous_name: 'Two names are alike, so typing one in a date may be ambiguous.',
  eras_leave_gap: 'There is a stretch of years that no era covers.',
  eras_share_start: 'Two eras start on the same day.',
};

export function issueText(issue: { readonly code: keyof typeof ISSUE_TEXTS }): string {
  return ISSUE_TEXTS[issue.code];
}

function unescapePointer(token: string): string {
  return token.replace(/~1/g, '/').replace(/~0/g, '~');
}

function indexOfToken(token: string | undefined): number | null {
  if (token === undefined || !/^\d+$/.test(token)) return null;
  return Number(token);
}

/** Where in the calendar a JSON pointer points: "Month 3 (Sowing)", "Season Winter", "Era 2"… */
export function issuePlace(pointer: string, definition: CalendarDefinition | null): string {
  const tokens = pointer.split('/').slice(1).map(unescapePointer);
  const [head, indexToken] = tokens;
  const index = indexOfToken(indexToken);
  if (head === 'months' && index !== null) {
    const name = definition?.months[index]?.name;
    return name ? `Month ${index + 1} (${name})` : `Month ${index + 1}`;
  }
  if (head === 'seasons' && index !== null) {
    const name = definition?.seasons[index]?.name;
    return name ? `Season ${name}` : `Season ${index + 1}`;
  }
  if (head === 'eras' && index !== null) return `Era ${index + 1}`;
  if (head === 'weekdays') return 'Weekdays';
  if (head === 'leap') return 'Leap rule';
  return 'The calendar';
}

const REVIEW_TEXTS: Record<TickReviewReason, string> = {
  calendar_invalid: "The calendar can't be read",
  unknown_key: 'It names a month, season or era the calendar lacks',
  incomplete_parts: "It doesn't give enough to make a date",
  day_out_of_range: "Its day doesn't exist in that month",
  time_out_of_range: "Its time doesn't exist in a day",
  week_out_of_range: "Its week doesn't exist in that month",
  not_in_year: "It isn't a date in that year",
  outside_era: 'It falls outside every era',
  inverted_range: 'Its end comes before its start',
  out_of_tick_range: 'It is too far from year zero to count',
  key_removed: 'Its month no longer exists',
};
// The list and the texts must name the same reasons.
for (const reason of TICK_REVIEW_REASONS) void REVIEW_TEXTS[reason];

export function reviewReasonText(reason: TickReviewReason): string {
  return REVIEW_TEXTS[reason];
}

// ------------------------------------------------------------------------------------- dates

/**
 * A tick result as the GM reads it: a day, or "<first day> – <last day>" at day precision.
 * `hi` is exclusive, so a span's last day is the one holding `hi - 1`.
 */
export function tickRangeText(calendar: CompiledCalendar, result: TickResultV1): string {
  const none = 'a date outside every era';
  const day = (tick: number) => formatTick(calendar, tick, 'day');
  switch (result.status) {
    case 'ok': {
      const lo = day(result.lo);
      if (result.hi - result.lo <= calendar.secondsPerDay) return lo ?? none;
      const hi = day(result.hi - 1);
      return lo === null || hi === null ? none : `${lo} – ${hi}`;
    }
    case 'open_lo': {
      const hi = day(result.hi - 1);
      return hi === null ? none : `until ${hi}`;
    }
    case 'open_hi': {
      const lo = day(result.lo);
      return lo === null ? none : `from ${lo}`;
    }
    default:
      return none;
  }
}

/** A date in a calendar's words, or the fixed text when the calendar can't be compiled. */
export function dateText(calendar: CompiledCalendar | null, result: TickResultV1): string {
  return calendar === null ? 'a date outside every era' : tickRangeText(calendar, result);
}

/** One "Moving" row: the before in the stored calendar's words, the after in the draft's. */
export function movedText(
  stored: CompiledCalendar | null,
  draft: CompiledCalendar | null,
  point: { readonly before: TickResultV1; readonly after: TickResultV1 },
): string {
  return `${dateText(stored, point.before)} → ${dateText(draft, point.after)}`;
}

// -------------------------------------------------------------------------------------- diff

/** 1200 as "1,200" (no `Intl`: the same text on every machine). */
export function formatCount(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function plural(n: number, one: string, many: string): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

export function diffSummary(diff: CalendarDiffV1): string {
  const moved = diff.moved.length;
  const lost = diff.becameInvalid.length;
  const found = diff.becameValid.length;
  if (moved === 0 && lost === 0 && found === 0) return 'No date moves.';
  const clauses = [
    moved > 0 ? plural(moved, 'date moves', 'dates move') : null,
    lost > 0 ? `${formatCount(lost)} can no longer be placed` : null,
    found > 0 ? `${formatCount(found)} can be placed again` : null,
    diff.stillInvalid.length > 0
      ? `${formatCount(diff.stillInvalid.length)} still can't be placed`
      : null,
    diff.unchanged > 0 ? plural(diff.unchanged, 'stays the same', 'stay the same') : null,
    diff.episodesChanged.length > 0
      ? plural(
          diff.episodesChanged.length,
          'time-bounded link changes',
          'time-bounded links change',
        )
      : null,
  ].filter((clause): clause is string => clause !== null);
  return `${clauses.join(', ')}.`;
}

// --------------------------------------------------------------------------------- removed keys

export const REMAP_KINDS = ['months', 'intercalary', 'seasons', 'eras'] as const;
export type RemapKind = (typeof REMAP_KINDS)[number];

export interface KeyName {
  readonly key: string;
  readonly name: string;
}

export interface RemovedKey extends KeyName {
  readonly kind: RemapKind;
  /** The keys of the same kind the draft has, where this one's dates may go. */
  readonly targets: readonly KeyName[];
}

function eraName(era: CalendarDefinition['eras'][number]): string {
  return era.name !== '' ? era.name : era.suffix !== '' ? era.suffix : 'Unnamed era';
}

function keysOfKind(definition: CalendarDefinition, kind: RemapKind): KeyName[] {
  switch (kind) {
    case 'months':
      return definition.months
        .filter((m) => m.intercalary !== true)
        .map(({ key, name }) => ({ key, name }));
    case 'intercalary':
      return definition.months
        .filter((m) => m.intercalary === true)
        .map(({ key, name }) => ({ key, name }));
    case 'seasons':
      return definition.seasons.map(({ key, name }) => ({ key, name }));
    case 'eras':
      return definition.eras.map((era) => ({ key: era.key, name: eraName(era) }));
  }
}

/** Every month, intercalary month, season or era of `from` that `to` lacks, kind by kind. */
export function removedKeys(from: CalendarDefinition, to: CalendarDefinition): RemovedKey[] {
  const out: RemovedKey[] = [];
  for (const kind of REMAP_KINDS) {
    const targets = keysOfKind(to, kind);
    const has = new Set(targets.map((t) => t.key));
    for (const entry of keysOfKind(from, kind)) {
      if (!has.has(entry.key)) out.push({ kind, ...entry, targets });
    }
  }
  return out;
}

/** A choice per removed key: a key of the draft, or null for "Nowhere (they need review)". */
export type RemapChoices = Partial<Record<RemapKind, Record<string, string | null>>>;

/** The remap of an edit: `inferRemap`, where "Nowhere" is no entry at all. */
export function remapFor(
  from: CalendarDefinition,
  to: CalendarDefinition,
  choices: RemapChoices,
): CalendarRemap {
  const renames: Record<string, Record<string, string>> = {};
  for (const kind of REMAP_KINDS) {
    const picked: Record<string, string> = {};
    for (const [key, target] of Object.entries(choices[kind] ?? {})) {
      if (target !== null) picked[key] = target;
    }
    renames[kind] = picked;
  }
  return inferRemap(from, to, renames as CalendarRemap);
}

// --------------------------------------------------------------------------------- draft key

function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      out[key] = stable((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

/** A string that identifies a draft whatever the order of its keys. */
export function draftKey(definition: unknown, remap: unknown): string {
  return JSON.stringify(stable({ definition, remap }));
}

// ----------------------------------------------------------------------------------- commit

export interface CommitInput {
  readonly calendar: CalendarV1 | null;
  readonly draft: CalendarDefinition;
  readonly remap: CalendarRemap;
  readonly diff: CalendarDiffV1 | null;
  /** The `draftKey` the diff was asked for. */
  readonly diffKey: string | null;
  readonly confirmLoss: boolean;
}

export type CommitReason =
  | 'Checking the changes…'
  | 'Fix the problems above to commit.'
  | 'Nothing has changed.'
  | "Confirm the dates that can't be placed."
  | null;

function hasRemapEntries(remap: CalendarRemap): boolean {
  return Object.values(remap).some((map) => map !== undefined && Object.keys(map).length > 0);
}

/** Whether "Commit calendar" is on, and when it is not, why. */
export function commitState(input: CommitInput): { enabled: boolean; reason: CommitReason } {
  const { calendar, draft, remap, diff, diffKey, confirmLoss } = input;
  const off = (reason: Exclude<CommitReason, null>) => ({ enabled: false, reason });
  if (calendar === null) {
    // The first calendar has no dates to move: local `compile` is the whole check.
    return compile(draft).ok
      ? { enabled: true, reason: null }
      : off('Fix the problems above to commit.');
  }
  const current =
    diff !== null && diffKey === draftKey(draft, remap) && diff.fromVersion === calendar.version;
  if (!current || diff === null) return off('Checking the changes…');
  if (diff.toErrors.length > 0) return off('Fix the problems above to commit.');
  if (!hasRemapEntries(remap) && draftKey(draft, {}) === draftKey(calendar.definition, {})) {
    return off('Nothing has changed.');
  }
  if (diff.becameInvalid.length > 0 && !confirmLoss) {
    return off("Confirm the dates that can't be placed.");
  }
  return { enabled: true, reason: null };
}
